import type { IReaction } from '@/api';
import { useCommentActions } from '@/api/hooks/useCommentActions';
import { useTranslation } from '@/app/_shared/hooks/useTranslation';
import { AvatarWithLine } from '@/features/comments/components/AvatarWithLine';
import { CommentActions } from '@/features/comments/components/CommentActions';
import { CommentContent } from '@/features/comments/components/CommentContent';
import { ReplySection } from '@/features/comments/components/ReplySection';
import { useReplyState } from '@/features/comments/hooks/useReplyState';
import type { CommentItemProps } from '@/features/comments/types';
import { useEffect, useState } from 'react';

export const CommentItem = ({
	comment,
	level = 0,
	onLike,
	onReply,
}: CommentItemProps) => {
	const { t } = useTranslation();

	const [isLiked, setIsLiked] = useState(comment.reacted);

	const { replyToComment } = useCommentActions(comment.conversationId);

	useEffect(() => {
		setIsLiked(comment.reacted);
	}, [comment.reacted]);

	const { isReplying, isHidden, toggleReply, toggleHide } = useReplyState();

	const handleLike = (data: IReaction) => {
		setIsLiked(!isLiked);
		onLike(comment.id, data);
	};

	const handleReply = (content: string, isReplyOfReply = false) => {
		replyToComment({ commentId: comment.id, content });
		if (!isReplyOfReply) {
			toggleReply();
		}
	};

	return (
		<div className="relative">
			<div className="flex gap-3">
				<AvatarWithLine
					avatar={comment.userAvatar}
					name={comment.userFullName}
					hasReplies={comment.replyCount > 0}
					isHidden={isHidden}
				/>
				<div className="flex-1 pt-1">
					<CommentContent
						author={{
							id: comment.userId,
							name: comment.userFullName,
						}}
						content={comment.content}
					/>

					<CommentActions
						onLike={handleLike}
						onReply={() => {
							if (isHidden) toggleHide();
							toggleReply();
						}}
						likes={comment.reactionCount}
						isLiked={isLiked}
						createdAt={comment.createdAt}
					/>

					{isHidden && comment.replyCount > 0 ? (
						<button
							type="button"
							onClick={toggleHide}
							className="text-[13px] text-[#8F8F8F] hover:text-green-600 mt-2 cursor-pointer"
						>
							{t('common:viewMore')} {comment.replyCount} replies
						</button>
					) : (
						<>
							<ReplySection
								commentId={comment.id}
								level={level}
								isReplying={isReplying}
								onReply={handleReply}
								onToggleHide={toggleHide}
								onLike={onLike}
								onReplyToComment={onReply}
							/>
							{comment.replyCount > 0 && (
								<button
									type="button"
									onClick={toggleHide}
									className="text-[13px] text-[#8F8F8F] hover:text-green-600 block cursor-pointer mt-2"
								>
									{t('common:hide')}
								</button>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};
